import { ArrowLeft, Ban, FileClock, Lock } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, buttonVariants, ErrorState, LoadingState, PageHeader, StatusBadge } from '@payment-plan/ui';
import { AnularSeparacionButton, CierreFeedback, DescartarBorradorButton, MENSAJE_ANULADA } from './CierreSeparacion';
import { DocumentsPanel } from './DocumentsPanel';
import { useAuth } from '../../../auth/auth-context';
import { ApiError } from '../../../lib/api-client';
import { can } from '../../../lib/permissions';
import type { SeparacionDetalle } from '../../../types/consultas';
import { mensajeDeError } from '../../separations/errors';
import { formatArea, formatCOP, formatFecha } from '../../separations/money';
import { rutaEditarSeparacion } from '../../separations/rutas';
import {
  ESTADO_ANULADA,
  ESTADO_BORRADOR,
  ESTADO_CORREGIDA,
  ESTADO_DESCARTADA,
  ESTADO_GENERADA,
  ESTADOS_ANULABLES,
} from '../estados';
import { etiquetaUnidad, formatInstante } from '../formato';
import type { AlcanceConsulta } from '../use-consultas';
import { useSeparationDetail } from '../use-consultas';

interface SeparationDetailProps {
  separacionId: string;
  alcance: AlcanceConsulta;
}

const DT_CLASS = 'text-xs font-semibold uppercase tracking-wide text-muted-foreground';
const DD_CLASS = 'mt-0.5 text-base font-medium text-foreground';
const SECCION_CLASS = 'animate-rise space-y-4 rounded-xl border border-border/70 bg-card p-5 shadow-elegant sm:p-6';
const TITULO_SECCION_CLASS = 'text-lg font-semibold tracking-tight text-foreground';

function avisoDeEstado(estado: SeparacionDetalle['estado']): string | null {
  if (estado === ESTADO_GENERADA) {
    return 'Plan de pagos generado: los datos quedaron bloqueados para edición. Para cambiarlos, usa «Corregir plan de pagos».';
  }
  if (estado === ESTADO_CORREGIDA) {
    return 'La separación está en corrección: la nueva versión del plan se emite desde el flujo del asesor.';
  }
  return null;
}

/**
 * Detalle de solo lectura de una separación (HU-C08 / HU-A05) con su panel de documentos.
 * Muestra los valores **congelados** de la separación (CA-6): nunca el precio de lista vigente.
 */
export function SeparationDetail({ separacionId, alcance }: SeparationDetailProps): React.JSX.Element {
  const { data: separacion, isLoading, isError, error, refetch } = useSeparationDetail(alcance, separacionId);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exito, setExito] = React.useState<string | null>(null);
  const [errorCierre, setErrorCierre] = React.useState<string | null>(null);
  const rutaListado = alcance === 'propias' ? '/separations' : '/admin/separations';
  const volver = (
    <Link to={rutaListado} className={buttonVariants({ variant: 'outline', size: 'lg', className: 'px-4' })}>
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Volver al listado
    </Link>
  );

  if (isLoading) return <LoadingState message="Cargando la separación" />;
  if (isError || !separacion) {
    const noEncontrada = error instanceof ApiError && error.status === 404;
    return (
      <div className="mx-auto w-full max-w-4xl space-y-4">
        <ErrorState
          title="No se pudo abrir la separación"
          message={noEncontrada ? 'La separación no existe o no tienes acceso a ella.' : mensajeDeError(error, 'cargar la separación')}
          onRetry={noEncontrada ? undefined : () => void refetch()}
        />
        <div className="flex justify-center">{volver}</div>
      </div>
    );
  }

  const aviso = avisoDeEstado(separacion.estado);
  // feat-67: cierre de la separación. El back decide permisos, alcance y matriz; aquí solo se ofrece.
  const permisos = user?.permissions;
  const puedeAnular = can(permisos, 'separacion.anular') && ESTADOS_ANULABLES.includes(separacion.estado);
  const borradorPropio = alcance === 'propias' && separacion.estado === ESTADO_BORRADOR;
  const alErrorCierre = (mensaje: string) => {
    setExito(null);
    setErrorCierre(mensaje);
  };
  const condiciones: Array<[string, string]> = [
    ['Precio total pactado', formatCOP(separacion.precioTotal)],
    ['Valor de la separación', formatCOP(separacion.valorSeparacion)],
    ['Cuota inicial', formatCOP(separacion.cuotaInicialTotal)],
    ['Saldo a crédito', formatCOP(separacion.saldoCredito)],
    ['Escrituración estimada', formatFecha(separacion.fechaEscrituracion)],
  ];

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <PageHeader
        eyebrow={alcance === 'propias' ? 'Mis separaciones' : 'Administración · Separaciones'}
        title={`Separación ${separacion.consecutivo ?? 'en borrador'}`}
        description={`${separacion.proyecto.nombre} · ${etiquetaUnidad(separacion.unidad)}`}
        action={volver}
      />

      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={separacion.estado} />
        <span className="text-sm text-muted-foreground">Separada el {formatFecha(separacion.fechaSeparacion)}</span>
        {puedeAnular && (
          <div className="ml-auto">
            <AnularSeparacionButton
              separacionId={separacion.id}
              // ADR-018: en la consulta administrativa el superadmin anula en la inmobiliaria de la separación.
              inmobiliariaId={alcance === 'admin' ? separacion.inmobiliaria?.id : undefined}
              onAnulada={() => {
                setErrorCierre(null);
                setExito(MENSAJE_ANULADA);
              }}
              onError={alErrorCierre}
            />
          </div>
        )}
      </div>

      <CierreFeedback exito={exito} error={errorCierre} />

      {borradorPropio && (
        <Alert variant="default">
          <FileClock className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            Separación en borrador: la unidad queda reservada mientras siga abierta. Continúala o descártala para
            liberar la unidad.
            <div className="mt-3 flex flex-wrap gap-2">
              {can(permisos, 'separacion.crear') && (
                <Link
                  to={rutaEditarSeparacion(separacion.id)}
                  className={buttonVariants({ variant: 'outline', size: 'lg', className: 'px-4' })}
                >
                  Continuar separación
                </Link>
              )}
              {can(permisos, 'separacion.descartar') && (
                <DescartarBorradorButton
                  separacionId={separacion.id}
                  onDescartada={() => void navigate('/separations')}
                  onError={alErrorCierre}
                />
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {separacion.estado === ESTADO_ANULADA && (
        <Alert variant="destructive" role="alert">
          <Ban className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            <span className="block font-semibold">
              Separación anulada el {separacion.anuladaEn ? formatInstante(separacion.anuladaEn) : '—'}.
            </span>
            <span className="block">Motivo: {separacion.motivoAnulacion ?? '—'}</span>
            <span className="block">
              La unidad quedó liberada. Los documentos emitidos se conservan en el historial y no se modifican.
            </span>
          </AlertDescription>
        </Alert>
      )}

      {separacion.estado === ESTADO_DESCARTADA && (
        <Alert variant="default">
          <Ban className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>Borrador descartado: la unidad quedó liberada.</AlertDescription>
        </Alert>
      )}

      {aviso && (
        <Alert variant={separacion.estado === ESTADO_GENERADA ? 'default' : 'warning'}>
          <Lock className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            {aviso}
            {/* Integración lote 02: el asesor retoma la corrección en el flujo (fix-50 · F-01). */}
            {separacion.estado === ESTADO_CORREGIDA && alcance === 'propias' && (
              <Link
                to={rutaEditarSeparacion(separacion.id)}
                className={buttonVariants({ variant: 'outline', size: 'lg', className: 'mt-3 px-4' })}
              >
                Continuar corrección
              </Link>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <section aria-labelledby="detalle-resumen" className={SECCION_CLASS}>
          <h2 id="detalle-resumen" className={TITULO_SECCION_CLASS}>
            Unidad y responsables
          </h2>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className={DT_CLASS}>Proyecto</dt>
              <dd className={DD_CLASS}>{separacion.proyecto.nombre}</dd>
            </div>
            <div>
              <dt className={DT_CLASS}>Unidad</dt>
              <dd className={DD_CLASS}>{etiquetaUnidad(separacion.unidad)}</dd>
            </div>
            <div>
              <dt className={DT_CLASS}>Área privada / construida</dt>
              <dd className={`${DD_CLASS} tabular-nums`}>
                {formatArea(separacion.unidad.areaPrivada)} / {formatArea(separacion.unidad.areaConstruida)}
              </dd>
            </div>
            <div>
              <dt className={DT_CLASS}>Matrícula</dt>
              <dd className={DD_CLASS}>{separacion.unidad.matricula}</dd>
            </div>
            <div>
              <dt className={DT_CLASS}>Adicionales</dt>
              <dd className={DD_CLASS}>
                {separacion.unidadesAdicionales.length === 0 ? (
                  'Ninguna'
                ) : (
                  <ul>
                    {separacion.unidadesAdicionales.map((u) => (
                      <li key={u.id}>{etiquetaUnidad(u)}</li>
                    ))}
                  </ul>
                )}
              </dd>
            </div>
            <div>
              <dt className={DT_CLASS}>Asesor</dt>
              <dd className={DD_CLASS}>{separacion.asesor.nombre}</dd>
            </div>
            {alcance === 'admin' && separacion.inmobiliaria && (
              <div>
                <dt className={DT_CLASS}>Inmobiliaria</dt>
                <dd className={DD_CLASS}>{separacion.inmobiliaria.nombre}</dd>
              </div>
            )}
            <div>
              <dt className={DT_CLASS}>Autorización de datos</dt>
              <dd className={DD_CLASS}>
                {separacion.autorizacionDatos && separacion.autorizacionFecha
                  ? `Registrada el ${formatInstante(separacion.autorizacionFecha)}`
                  : 'Sin registrar'}
              </dd>
            </div>
          </dl>
        </section>

        <section aria-labelledby="detalle-condiciones" className={SECCION_CLASS}>
          <h2 id="detalle-condiciones" className={TITULO_SECCION_CLASS}>
            Condiciones pactadas
          </h2>
          <p className="text-sm text-muted-foreground">
            Valores con los que se registró la separación. Un cambio posterior del precio de lista no los modifica.
          </p>
          <dl className="grid gap-4 sm:grid-cols-2">
            {condiciones.map(([dt, dd]) => (
              <div key={dt}>
                <dt className={DT_CLASS}>{dt}</dt>
                <dd className={`${DD_CLASS} whitespace-nowrap font-semibold tabular-nums`}>{dd}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <section aria-labelledby="detalle-separantes" className={SECCION_CLASS}>
        <h2 id="detalle-separantes" className={TITULO_SECCION_CLASS}>
          Separantes
        </h2>
        <ul className="grid gap-4 lg:grid-cols-2">
          {separacion.separantes.map((s) => (
            <li key={s.id} className="rounded-lg border border-border/60 p-4">
              <p className="font-semibold text-foreground">{s.nombre}</p>
              <p className="text-sm text-muted-foreground">
                {s.tipoPersona === 'JURIDICA' ? 'Persona jurídica' : 'Persona natural'} · {s.tipoDocumento}{' '}
                {s.numeroDocumento} · {s.porcentaje} %
              </p>
              <p className="mt-2 text-sm">{s.correo}</p>
              <p className="text-sm">{s.celular}</p>
            </li>
          ))}
        </ul>
      </section>

      {separacion.cuotas.length > 0 && (
        <section aria-labelledby="detalle-cuotas" className={SECCION_CLASS}>
          <h2 id="detalle-cuotas" className={TITULO_SECCION_CLASS}>
            Cuotas
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border/70">
            <table aria-label="Cuotas del plan" className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  {['N.º', 'Vencimiento', 'Tipo', 'Valor'].map((h) => (
                    <th key={h} scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {separacion.cuotas.map((c) => (
                  <tr key={`${c.numero}-${c.fechaVencimiento}`} className="border-t border-border/60">
                    <td className="px-3 py-2 tabular-nums">{c.numero}</td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">{formatFecha(c.fechaVencimiento)}</td>
                    <td className="px-3 py-2">{c.tipo === 'EXTRAORDINARIA' ? 'Extraordinaria' : 'Ordinaria'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatCOP(c.valor)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/30 font-semibold">
                  <td className="px-3 py-2" colSpan={3}>
                    Total cuotas
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatCOP(separacion.totalCuotas)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}

      <DocumentsPanel separacion={separacion} alcance={alcance} />
    </div>
  );
}
